import React from 'react';
import { darkTourismTypologies, myths } from '../data/mockData';
import { BookOpen, AlertCircle, Map, Layers } from 'lucide-react';

const DarkTourismExplained = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/xiao-en-memorial-park.jpg"
            alt="Dark Tourism Explained"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-[#1a1c1b]"></div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="container-custom pb-20 md:pb-32">
            <div className="max-w-4xl">
              <h1 className="text-white font-light text-5xl md:text-7xl mb-6 leading-tight">
                Understanding Dark Tourism
              </h1>
              <p className="text-lg md:text-xl font-light text-white/90 mb-8 leading-relaxed max-w-2xl">
                A comprehensive guide to the history, significance, and cultural context 
                of dark tourism in Malaysia and beyond.
              </p>
            </div>
          </div>
        </div>

        {/* Decorative Ground Element */}
        <div className="absolute bottom-0 left-0 right-0 h-32 md:h-48">
          <img 
            src="/images/villa-nabila-johor-1.jpg" 
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
        </div>
      </section>

      {/* What is Dark Tourism */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <BookOpen className="text-[#dc2626]" size={48} />
              <h2 className="heading-2 text-[#dc2626]">What is Dark Tourism?</h2>
            </div>
            
            <div className="space-y-6 body-medium text-white/80">
              <p className="text-white/90 text-xl">
                Dark tourism is the act of visiting places associated with death, tragedy, and the 
                darker chapters of human history. It is not about seeking shock or thrill, but about 
                engaging with the past—reflecting on human resilience, the consequences of conflict, 
                and the stories that shape societies.
              </p>
              
              <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626] my-8">
                <p className="text-white/90 italic">
                  "And if the idea feels unusual, consider this: how many times have you visited a 
                  battlefield, a memorial, a cemetery, or even a museum exhibit about war? Chances are, 
                  you've already had a 'dark tourist' moment without labeling it as such."
                </p>
              </div>

              <h3 className="heading-3 text-[#dc2626] mt-12 mb-4">A Tradition Older Than We Think</h3>
              <p>
                Dark tourism is not a modern invention. For centuries, people have been drawn to places 
                marked by death and tragedy. Ancient Romans visited gladiatorial arenas. European nobility 
                toured the ruins of Pompeii. Battlefield tourism flourished after Waterloo in 1815. 
                Victorian Londoners queued to view bodies at the Paris Morgue.
              </p>
              <p>
                Religious pilgrimages to sites of martyrdom represent some of the oldest forms of dark 
                tourism. From the Hajj to Jerusalem, people have long journeyed to places connected to 
                suffering and sacrifice.
              </p>

              <h3 className="heading-3 text-[#dc2626] mt-12 mb-4">Dark Tourism vs. Heritage Tourism</h3>
              <p>
                Not every historic landmark or pilgrimage destination belongs within the scope of dark 
                tourism. A fort built for defense may simply be a heritage site unless it witnessed 
                significant conflict or loss. A colonial building may be admired for its architecture 
                without engaging with the darker aspects of colonial rule.
              </p>
              <p className="text-white/90 font-semibold">
                Dark tourism focuses specifically on places where memory of conflict, tragedy, or human 
                struggle forms a central part of their meaning.
              </p>
            </div>
          </div>

          {/* Image with CTA */}
          <div className="mt-16 relative rounded-lg overflow-hidden">
            <img 
              src="/images/penang-war-museum-1.jpg"
              alt="Historical Site"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <h3 className="heading-3 text-white mb-4">Explore Malaysia's Dark Tourism Sites</h3>
              <p className="body-medium text-white/90 mb-6 max-w-2xl">
                Discover over 250 documented sites across Malaysia connected to war, conflict, and memory.
              </p>
              <a href="/explore" className="btn-primary">
                View All Sites
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <div className="flex items-center gap-4 mb-8">
              <AlertCircle className="text-[#dc2626]" size={48} />
              <h2 className="heading-2 text-[#dc2626]">Why It Matters</h2>
            </div>
            
            <div className="space-y-6 body-medium text-white/80">
              <p className="text-white/90 text-xl">
                Understanding places connected to difficult histories is not about focusing on tragedy. 
                It is about understanding how the past continues to shape the present.
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                  <h4 className="heading-4 text-[#dc2626] mb-3">Historical Awareness</h4>
                  <p className="body-small text-white/70">
                    These sites offer insights into events that shaped communities, nations, and the 
                    world. They remind us that history is not just a sequence of dates and achievements—it 
                    includes struggle, loss, and survival.
                  </p>
                </div>

                <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                  <h4 className="heading-4 text-[#dc2626] mb-3">Cultural Continuity</h4>
                  <p className="body-small text-white/70">
                    Places of memory help communities maintain connection to their past. They preserve 
                    stories that might otherwise be forgotten and ensure that lessons are passed down 
                    through generations.
                  </p>
                </div>

                <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                  <h4 className="heading-4 text-[#dc2626] mb-3">National History</h4>
                  <p className="body-small text-white/70">
                    Dark tourism sites contribute to a more complete understanding of Malaysia's history. 
                    They show that the nation's identity is shaped not only by independence and development 
                    but also by colonialism, war, migration, and resilience.
                  </p>
                </div>

                <div className="bg-[#302f2c] p-6 rounded-lg border border-[#3a2222]">
                  <h4 className="heading-4 text-[#dc2626] mb-3">Opportunities for Reflection</h4>
                  <p className="body-small text-white/70">
                    These sites invite contemplation. They encourage visitors to consider questions of 
                    justice, memory, and human behavior—often in ways that are deeply personal.
                  </p>
                </div>
              </div>

              <p className="text-white/90 italic border-l-4 border-[#dc2626] pl-6">
                "Ultimately, recognizing these places is about balance. It is about acknowledging both 
                the achievements and the hardships that have shaped Malaysia."
              </p>
            </div>
          </div>

          {/* Image with CTA */}
          <div className="mt-16 relative rounded-lg overflow-hidden">
            <img 
              src="/images/fort-margarita-kuching.jpg"
              alt="Colonial Heritage"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent"></div>
            <div className="absolute inset-0 p-8 md:p-12 flex items-center">
              <div className="max-w-2xl">
                <h3 className="heading-3 text-white mb-4">Visit Respectfully</h3>
                <p className="body-medium text-white/90 mb-6">
                  Learn how to approach dark tourism sites with cultural sensitivity and respect for Malaysia's heritage.
                </p>
                <a href="/guidelines" className="btn-primary">
                  Read Visitor Guidelines
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Typologies */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-6xl">
            <div className="flex items-center gap-4 mb-8">
              <Layers className="text-[#dc2626]" size={48} />
              <h2 className="heading-2 text-[#dc2626]">Dark Tourism Typologies</h2>
            </div>
            
            <p className="body-medium text-white/80 mb-12 max-w-4xl">
              Dark tourism encompasses a wide range of site types, each connected to different forms of 
              death, tragedy, or human suffering. Below is a comprehensive typology based on established 
              academic frameworks.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {darkTourismTypologies.slice(0, 10).map((type) => (
                <div 
                  key={type.id}
                  className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222] hover:border-[#dc2626]/50 transition-colors"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-[#dc2626] font-bold text-xl">{type.id}</span>
                    <h3 className="heading-4 text-white">{type.name}</h3>
                  </div>
                  <p className="body-small text-white/70 mb-3">{type.description}</p>
                  <p className="caption text-[#888680]">
                    <strong>Malaysia Examples:</strong> {type.examples}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA Image */}
            <div className="mt-16 relative rounded-lg overflow-hidden">
              <img 
                src="/images/old-courthouse-kuching.jpg"
                alt="War Memorial"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <h3 className="heading-3 text-white mb-4">Plan Your Dark Tourism Journey</h3>
                <p className="body-medium text-white/90 mb-6 max-w-2xl">
                  Discover themed itineraries and routes to explore Malaysia's heritage sites respectfully.
                </p>
                <a href="/itineraries" className="btn-primary">
                  View Itineraries
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Levels of Darkness */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-5xl">
            <h2 className="heading-2 text-[#dc2626] mb-8">Levels of Darkness</h2>
            
            <div className="space-y-6 body-medium text-white/80 mb-12">
              <p>
                Not all dark tourism sites carry the same emotional weight. Scholars have proposed that 
                sites can be understood along a spectrum, ranging from "lightest" to "darkest" forms of dark tourism.
              </p>
            </div>

            {/* Visual Spectrum - matching reference image */}
            <div className="bg-[#f5f3ef] rounded-xl p-8 md:p-12" data-testid="darkness-spectrum">
              {/* Top labels */}
              <div className="flex justify-between mb-2 px-4">
                <span className="text-sm font-medium" style={{ color: '#7a9e4a' }}>Lightest</span>
                <span className="text-sm font-medium" style={{ color: '#5a3a6e' }}>Darkest</span>
              </div>

              {/* Gradient line with dots */}
              <div className="relative mx-4 mb-2">
                {/* Horizontal gradient line */}
                <div className="h-1 rounded-full" style={{ background: 'linear-gradient(to right, #c8dea0, #8cbcaa, #8a9cc0, #a080b8, #5a3a6e)' }}></div>
                
                {/* Arrow indicators */}
                <div className="absolute top-1/2 -translate-y-1/2 -left-3 text-sm" style={{ color: '#7a9e4a' }}>&#x2190;</div>
                <div className="absolute top-1/2 -translate-y-1/2 -right-3 text-sm" style={{ color: '#5a3a6e' }}>&#x2192;</div>

                {/* Dots positioned on the line */}
                <div className="absolute top-1/2 -translate-y-1/2 left-[10%] w-4 h-4 rounded-full border-2 border-white shadow-md" style={{ background: '#c8dea0' }}></div>
                <div className="absolute top-1/2 -translate-y-1/2 left-[30%] w-4 h-4 rounded-full border-2 border-white shadow-md" style={{ background: '#8cbcaa' }}></div>
                <div className="absolute top-1/2 -translate-y-1/2 left-[50%] w-4 h-4 rounded-full border-2 border-white shadow-md" style={{ background: '#8a9cc0' }}></div>
                <div className="absolute top-1/2 -translate-y-1/2 left-[70%] w-4 h-4 rounded-full border-2 border-white shadow-md" style={{ background: '#a080b8' }}></div>
                <div className="absolute top-1/2 -translate-y-1/2 left-[90%] w-4 h-4 rounded-full border-2 border-white shadow-md" style={{ background: '#5a3a6e' }}></div>
              </div>

              {/* Level labels below dots */}
              <div className="grid grid-cols-5 text-center mb-6 mt-6">
                <span className="text-sm font-semibold text-[#444]">Lightest</span>
                <span className="text-sm font-semibold text-[#444]">Light</span>
                <span className="text-sm font-semibold text-[#444]">Medium</span>
                <span className="text-sm font-semibold text-[#444]">Dark</span>
                <span className="text-sm font-semibold text-[#444]">Darkest</span>
              </div>

              {/* Description cards with triangles */}
              <div className="grid grid-cols-5 gap-2 md:gap-3">
                {/* Lightest */}
                <div className="relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: '#eef5dd' }}></div>
                  <div className="rounded-lg p-3 md:p-4 text-xs md:text-sm leading-relaxed" style={{ background: '#eef5dd', color: '#444' }}>
                    Burial sites of historical figures; homes or museums displaying their personal history
                  </div>
                </div>

                {/* Light */}
                <div className="relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: '#dff0e8' }}></div>
                  <div className="rounded-lg p-3 md:p-4 text-xs md:text-sm leading-relaxed" style={{ background: '#dff0e8', color: '#444' }}>
                    Locations connected to symbolic death or hardship in history
                  </div>
                </div>

                {/* Medium */}
                <div className="relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: '#e4e4f0' }}></div>
                  <div className="rounded-lg p-3 md:p-4 text-xs md:text-sm leading-relaxed" style={{ background: '#e4e4f0', color: '#444' }}>
                    Former prisons, battlefields, sites of disaster, memorials
                  </div>
                </div>

                {/* Dark */}
                <div className="relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: '#d4c4e0' }}></div>
                  <div className="rounded-lg p-3 md:p-4 text-xs md:text-sm leading-relaxed" style={{ background: '#d4c4e0', color: '#444' }}>
                    Disaster areas, wartime locations, places of mass deaths
                  </div>
                </div>

                {/* Darkest */}
                <div className="relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-b-[8px] border-l-transparent border-r-transparent" style={{ borderBottomColor: '#b8a0cc' }}></div>
                  <div className="rounded-lg p-3 md:p-4 text-xs md:text-sm leading-relaxed" style={{ background: '#b8a0cc', color: '#fff' }}>
                    Sites of deliberate killing, genocide, or massacre
                  </div>
                </div>
              </div>

              {/* Bottom labels */}
              <div className="grid grid-cols-5 text-center mt-4">
                <span className="text-xs font-medium text-[#555]">Lightest</span>
                <span className="text-xs font-medium text-[#555]">Light</span>
                <span className="text-xs font-medium text-[#555]">Medium</span>
                <span className="text-xs font-medium text-[#555]">Dark</span>
                <span className="text-xs font-medium text-[#555]">Darkest</span>
              </div>
            </div>

            <div className="space-y-6 body-medium text-white/80 mt-12">
              <p>
                This framework helps us understand why some sites feel more emotionally challenging than 
                others and guides appropriate approaches to visitation and interpretation.
              </p>
              
              <p className="caption text-[#888680] italic">
                This interpretation is informed by established academic research on dark tourism, 
                particularly the work of Stone (2006) and Sharpley (2009).
              </p>
            </div>
          </div>

          {/* Image with CTA */}
          <div className="mt-16 relative rounded-lg overflow-hidden">
            <img 
              src="/images/kellies-castle.jpg"
              alt="Memorial Site"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent"></div>
            <div className="absolute inset-0 p-8 md:p-12 flex items-center">
              <div className="max-w-2xl">
                <h3 className="heading-3 text-white mb-4">Share Your Story</h3>
                <p className="body-medium text-white/90 mb-6">
                  Have you visited a dark tourism site? Help preserve Malaysia's heritage by sharing your experience.
                </p>
                <a href="/share-story" className="btn-primary">
                  Contribute Your Story
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Myths */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-2 text-[#dc2626] mb-8">Common Myths About Dark Tourism in Malaysia</h2>
            
            <p className="body-medium text-white/80 mb-12">
              Several misconceptions surround dark tourism, particularly in the Malaysian context. 
              Let's address the most common ones.
            </p>

            <div className="space-y-6">
              {myths.map((item) => (
                <div 
                  key={item.id}
                  className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222]"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <AlertCircle className="text-[#ff6b6b] flex-shrink-0" size={24} />
                    <div>
                      <h3 className="heading-4 text-[#ff6b6b] mb-2">Myth #{item.id}</h3>
                      <p className="body-medium text-white/90 font-semibold">{item.myth}</p>
                    </div>
                  </div>
                  <div className="ml-10 pl-4 border-l-2 border-[#dc2626]">
                    <p className="caption text-[#dc2626] mb-2">REALITY:</p>
                    <p className="body-medium text-white/80">{item.reality}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Public Perception in Malaysia */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl">
            <h2 className="heading-2 text-[#dc2626] mb-8">Public Perception in Malaysia</h2>
            
            <div className="space-y-6 body-medium text-white/80">
              <p className="text-white/90 text-xl italic">
                "Ask someone in Malaysia what dark tourism is and many will not recognize the term. 
                Ask about haunted places, however, and the response is immediate."
              </p>

              <p>
                This distinction is significant. In Malaysia, sites of tragedy are often perceived through 
                the lens of the supernatural rather than history. Old hospitals become "haunted." Abandoned 
                colonial buildings are feared rather than preserved. Cemeteries are avoided rather than 
                appreciated as historical archives.
              </p>

              <div className="bg-[#302f2c] p-8 rounded-lg my-8">
                <h4 className="heading-4 text-[#dc2626] mb-4">Cultural Influences on Perception</h4>
                <ul className="space-y-4">
                  <li>
                    <strong className="text-white">Fear and Avoidance:</strong> Many Malaysians prefer not 
                    to visit places connected to death, viewing them as spiritually dangerous or emotionally distressing.
                  </li>
                  <li>
                    <strong className="text-white">Online Fascination:</strong> Paradoxically, there's high 
                    engagement with "haunted" content online, but primarily as entertainment rather than education.
                  </li>
                  <li>
                    <strong className="text-white">Religious Perspectives:</strong> Different communities 
                    have varying beliefs about engaging with sites of death, from Islamic views on grave 
                    visitation to Chinese ancestral worship practices.
                  </li>
                </ul>
              </div>

              <p className="text-[#dc2626] font-semibold text-lg border-l-4 border-[#dc2626] pl-6">
                When sites are presented primarily as haunted or sensational locations, they tend to be 
                consumed as entertainment from a distance. When presented through history, memory, and 
                respectful storytelling, they become easier to approach and understand as part of shared heritage.
              </p>
            </div>
          </div>

          {/* Final CTA */}
          <div className="mt-16 relative rounded-lg overflow-hidden">
            <img 
              src="/images/wang-kelian-perlis.jpg"
              alt="Heritage Site"
              className="w-full h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
              <h3 className="heading-3 text-white mb-4">Learn More About Our Mission</h3>
              <p className="body-medium text-white/90 mb-6 max-w-2xl">
                Discover how we're documenting and preserving Malaysia's dark tourism heritage through research and education.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="/about" className="btn-primary">
                  About Us
                </a>
                <a href="/contact" className="btn-secondary">
                  Get in Touch
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DarkTourismExplained;
